<div class="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
    <header class="space-y-4">
        <h1 class="text-3xl font-bold text-green-800 border-b-2 border-green-800 pb-4">Vulnerability Disclosure Program</h1>
        
        <div class="bg-white rounded-lg shadow-sm p-6">
            <h2 class="text-xl font-semibold text-green-800 mb-4">Purpose of the Vulnerability Disclosure Program</h2>
            <div class="space-y-4">
                <p>The security of our systems is of utmost importance to us. Despite our best efforts to maintain a secure environment, vulnerabilities can still exist.</p>
                <p>We value the insights of the security community and encourage responsible disclosure of potential vulnerabilities. Our Vulnerability Disclosure Program allows you to responsibly share your findings with us.</p>
                <p>If you discover a potential vulnerability in any of our systems, services, or products, please notify us as soon as possible. Follow the process outlined below under "How to Disclose a Vulnerability."</p>
                <p>The purpose of this program is to receive, assess, and remediate cybersecurity vulnerabilities. We encourage security researchers and professionals to report vulnerabilities. Please note, this program is not for general inquiries unrelated to security vulnerabilities.</p>
            </div>
        </div>
    </header>

    <section class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-xl font-semibold text-green-800 mb-4">Program Scope</h2>
        <p class="mb-4">Our Vulnerability Disclosure Program covers:</p>
        <ul class="space-y-2">
            <li class="flex items-start">
                <svg class="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
                <span>Any product or service owned by us to which you have legal access.</span>
            </li>
            <li class="flex items-start">
                <svg class="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
                <span>Any product, service, and infrastructure we provide to shared service partners to which you have legal access.</span>
            </li>
        </ul>
    </section>

    <section class="bg-white rounded-lg shadow-sm p-6 border-l-4 border-red-500">
        <h2 class="text-xl font-semibold text-green-800 mb-4">Disallowed Activities</h2>
        <p class="mb-4">To ensure the integrity of the program, the following research activities are disallowed:</p>
        <div class="md:columns-2 gap-8">
            <ul class="list-disc pl-5 space-y-2">
                <li>Social engineering or phishing</li>
                <li>Denial of Service (DoS) or Distributed DoS (DDoS) attacks</li>
                <li>Physical attacks</li>
                <li>Attempts to modify or destroy data</li>
                <li>Clickjacking</li>
                <li>Accessing or attempting to access accounts or data that do not belong to you</li>
                <li>Any activity that violates any law</li>
                <li>Posting, transmitting, uploading, linking to, or sending any malware</li>
                <li>Automated vulnerability scan reports</li>
                <li>Leveraging deceptive techniques</li>
                <li>Exfiltrating any data under any circumstances</li>
                <li>Testing third-party websites, applications, or services that integrate with our services or products</li>
                <li>Disclosure of known public files or directories</li>
                <li>Reporting lack of Secure or HTTP Only flags on non-sensitive cookies</li>
                <li>Usage of a known vulnerable library or framework without a valid attack scenario</li>
            </ul>
        </div>
        
        <div class="mt-6 bg-gray-100 p-4 rounded-lg">
            <p class="font-medium mb-2">Do not report security vulnerabilities related to missing security controls or protections that are not directly exploitable, such as:</p>
            <ul class="list-disc pl-5 space-y-2">
                <li>Weak, insecure, or misconfigured SSL (secure sockets layer) or TLS (transport layer security) certificates</li>
                <li>Misconfigured DNS (domain name system) records, including SPF (sender policy framework) and DMARC (domain-based message authentication reporting and conformance)</li>
                <li>Missing security HTTP (hypertext transfer protocol) headers</li>
                <li>Theoretical cross-site request forgery and cross-site framing attacks</li>
            </ul>
        </div>
    </section>

    <section class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-xl font-semibold text-green-800 mb-4">How to Disclose a Vulnerability</h2>
        <div class="space-y-6">
            <p>To report a potential security vulnerability, please email: <a href="mailto:security@sencha-tea.com" class="text-green-800 hover:text-green-600 underline font-semibold">security&#64;sencha-tea.com</a></p>
            <p>Do not use LinkedIN or email to make direct reports with staff members as this will delay the process.</p>
            <p>Please include as much information as possible:</p>
            <ul class="space-y-2">
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Details of the potential security vulnerability</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>List of potentially affected products and services (if possible)</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>CVSS Vector String (CVSS 3.1 or greater preferred)</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>CVSS Risk Score</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Steps to reproduce the vulnerability</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Proof-of-concept code (if applicable)</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Names of any test accounts you have created (if applicable)</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Your contact details (optional)</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Whether you would like public acknowledgment for your contribution and the name you would like to be acknowledged under</span>
                </li>
            </ul>
        </div>
    </section>

    <section class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-xl font-semibold text-green-800 mb-4">Post-Disclosure Process</h2>
        <div class="space-y-4">
            <p>When you report a vulnerability, we will:</p>
            <ul class="space-y-2">
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Respond to you within 2 business days</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Recognise your contribution to our program if you choose public acknowledgment for your contribution</span>
                </li>
            </ul>
            <p>Please note:</p>
            <ul class="space-y-2">
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                    <span>We will not financially compensate you for reporting.</span>
                </li>
                <li class="flex items-start">
                    <svg class="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                    <span>We will not share your details with any other organisation without your permission.</span>
                </li>
            </ul>
        </div>
    </section>

    <section class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-xl font-semibold text-green-800 mb-4">Acknowledgments</h2>
        <p>With your permission, we will publish the names or aliases of people who contribute to our Vulnerability Disclosure Program in the Security Hall of Fame</p>
    </section>
</div>